import { useState } from "react";
import { Plus, Loader2, FileText } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import type { CcProject } from "@shared/schema";
import { ProjectFormModal } from "@/components/project-form-modal";
import { StrategyViewerModal } from "@/components/strategy-viewer-modal";

const PROJECT_TYPE_COLORS = {
  crm: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
  website: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400",
  marketing: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400",
  infrastructure: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
  business_operations: "bg-gray-100 text-gray-700 dark:bg-gray-950 dark:text-gray-400",
};

const PRIORITY_COLORS = {
  critical: "bg-red-600 hover:bg-red-700",
  high: "bg-orange-600 hover:bg-orange-700",
  medium: "bg-blue-600 hover:bg-blue-700",
  low: "bg-gray-600 hover:bg-gray-700",
};

export default function ControlCentreProjects() {
  const [, navigate] = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<CcProject | undefined>();
  const [strategyViewerOpen, setStrategyViewerOpen] = useState(false);
  const [selectedProjectForStrategy, setSelectedProjectForStrategy] = useState<CcProject | undefined>();

  const { data: projects = [], isLoading, isError, error } = useQuery<CcProject[]>({
    queryKey: ["/api/control-centre/projects"],
  });

  const handleAddProject = () => {
    setSelectedProject(undefined);
    setIsModalOpen(true);
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/project-manager/projects/${projectId}`);
  };

  const handleViewStrategy = (e: React.MouseEvent, project: CcProject) => {
    e.stopPropagation();
    setSelectedProjectForStrategy(project);
    setStrategyViewerOpen(true);
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Projects"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Project Manager" }, { label: "Projects" }]}
        actions={
          <Button onClick={handleAddProject} data-testid="button-add-project">
            <Plus className="mr-2 h-4 w-4" />
            Add Project
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <Card className="mx-auto max-w-2xl p-8 text-center">
            <p className="text-destructive font-medium">Failed to load projects</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : "An error occurred"}
            </p>
          </Card>
        ) : projects.length === 0 ? (
          <Card className="mx-auto max-w-2xl p-8 text-center">
            <p className="text-muted-foreground">No projects found</p>
          </Card>
        ) : (
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <Card 
                  key={project.id}
                  className="hover-elevate cursor-pointer h-full" 
                  data-testid={`card-project-${project.id}`}
                  onClick={() => handleViewProject(project.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge variant="secondary" className={PROJECT_TYPE_COLORS[project.projectType]}>
                        {project.projectType.replace("_", " ")}
                      </Badge>
                      <Badge className={PRIORITY_COLORS[project.priority]}>
                        {project.priority}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{project.projectName}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-semibold">{project.progressPercentage}%</span>
                      </div>
                      <Progress value={project.progressPercentage} className="h-2" />
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Target End</span>
                      <span className="font-medium">{project.targetEndDate || "Not set"}</span>
                    </div>

                    {project.strategyDocument && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={(e) => handleViewStrategy(e, project)}
                          data-testid={`button-view-strategy-${project.id}`}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          View Strategy
                        </Button>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground pt-2 border-t">
                      Click to view project dashboard
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <ProjectFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        project={selectedProject}
      />

      <StrategyViewerModal
        open={strategyViewerOpen}
        onOpenChange={setStrategyViewerOpen}
        projectName={selectedProjectForStrategy?.projectName || ""}
        strategyDocument={selectedProjectForStrategy?.strategyDocument}
      />
    </div>
  );
}
